import { API_CONFIG, getApiUrl, STORAGE_KEYS, HTTP_METHODS } from './config.ts';
import type { PaginatedResponse } from './types';
import { redirectToLogin } from '../auth/loginRedirect.ts';

// Custom error class for API errors
export class ApiClientError extends Error {
  public statusCode: number;
  public error: string;
  public details?: unknown;
  public errorId?: string;
  public correlationId?: string;
  public networkMethod?: string;
  public networkPath?: string;
  public networkErrorType?: 'http' | 'network' | 'timeout' | 'authentication';

  constructor(
    statusCode: number,
    error: string,
    details?: unknown,
    errorId?: string,
    correlationId?: string,
    networkMethod?: string,
    networkPath?: string,
    networkErrorType?: 'http' | 'network' | 'timeout' | 'authentication',
  ) {
    super(error);
    this.statusCode = statusCode;
    this.error = error;
    this.details = details;
    this.errorId = errorId;
    this.correlationId = correlationId;
    this.networkMethod = networkMethod;
    this.networkPath = networkPath;
    this.networkErrorType = networkErrorType;
    this.name = 'ApiClientError';
  }
}

// Request options interface
interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
  timeout?: number;
  suppressErrorCapture?: boolean;
  responseType?: 'json' | 'blob';
}

// Get stored tokens
const getAccessToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

const getRefreshToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

// Store tokens
export const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
};

// Clear tokens
export const clearTokens = (): void => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
};

export const handleUnauthorizedResponse = (status: number): boolean => {
  if (status !== 401) return false;

  clearTokens();
  redirectToLogin();
  return true;
};

// Request timeout handler
const fetchWithTimeout = async (
  url: string,
  options: RequestOptions = {}
): Promise<Response> => {
  const { timeout = API_CONFIG.TIMEOUT, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    const method = String(fetchOptions.method ?? HTTP_METHODS.GET).toUpperCase();
    const path = new URL(url, window.location.origin).pathname;
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiClientError(408, 'Request timeout', undefined, undefined, undefined, method, path, 'timeout');
    }
    throw new ApiClientError(0, 'Network request failed', undefined, undefined, undefined, method, path, 'network');
  }
};

const captureApiError = (error: unknown, suppressed: boolean): void => {
  if (!suppressed) window.dispatchEvent(new CustomEvent('app-api-error', { detail: error }));
};

// Refresh access token
const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(getApiUrl('/auth/refresh'), {
      method: HTTP_METHODS.POST,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearTokens();
      return false;
    }

    const data: { accessToken: string; refreshToken: string } = await response.json();
    
    if (data.accessToken && data.refreshToken) {
      setTokens(data.accessToken, data.refreshToken);
      return true;
    }

    clearTokens();
    return false;
  } catch (error) {
    clearTokens();
    return false;
  }
};

// Main API client function
async function apiClient<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, suppressErrorCapture = false, responseType = 'json', ...fetchOptions } = options;

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Correlation-ID': crypto.randomUUID(),
    ...(fetchOptions.headers as Record<string, string>),
  };

  // Add authorization header if not skipped
  if (!skipAuth) {
    const accessToken = getAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
  }

  // Make request
  const url = getApiUrl(endpoint);
  let response: Response;
  try {
    response = await fetchWithTimeout(url, { ...fetchOptions, headers });
  } catch (error) {
    captureApiError(error, suppressErrorCapture);
    throw error;
  }

  // Handle 401 - try to refresh token and retry once
  if (response.status === 401 && !skipAuth) {
    const refreshed = await refreshAccessToken();
    
    if (refreshed) {
      // Retry request with new token
      const newToken = getAccessToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
      }
      
      try {
        response = await fetchWithTimeout(url, { ...fetchOptions, headers });
      } catch (error) {
        captureApiError(error, suppressErrorCapture);
        throw error;
      }
    } else {
      // Refresh failed, clear tokens and return to this page after sign-in.
      clearTokens();
      redirectToLogin();
      const authError = new ApiClientError(
        401, 'Authentication failed. Please login again.', undefined, undefined, undefined,
        String(fetchOptions.method ?? HTTP_METHODS.GET).toUpperCase(), new URL(url, window.location.origin).pathname,
        'authentication',
      );
      captureApiError(authError, suppressErrorCapture);
      throw authError;
    }
  }

  // A refreshed token can still be rejected (for example, when the account was
  // disabled). Do not attempt another refresh; require a new sign-in instead.
  if (!skipAuth) handleUnauthorizedResponse(response.status);

  if (response.ok && responseType === 'blob') {
    return await response.blob() as T;
  }

  // Parse response
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  let data: any;
  if (isJson) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  // Handle error responses
  if (!response.ok) {
    // Handle both formats: {message: ...} and {error: ...}
    let errorMessage = 'An error occurred';
    let errorDetails = undefined;
    
    if (isJson && data) {
      // Try different error message fields
      errorMessage = data.message || data.error || data.statusText || errorMessage;
      errorDetails = data.details;
    } else {
      errorMessage = data || response.statusText || errorMessage;
    }
    
    const apiError = new ApiClientError(
      response.status,
      errorMessage,
      errorDetails,
      isJson ? data?.errorId : undefined,
      isJson ? data?.correlationId : response.headers.get('X-Correlation-ID') ?? undefined,
      String(fetchOptions.method ?? HTTP_METHODS.GET).toUpperCase(),
      new URL(url, window.location.origin).pathname,
      'http',
    );
    captureApiError(apiError, suppressErrorCapture);
    throw apiError;
  }

  return data as T;
}

// Convenience methods
export const api = {
  get: <T = any>(endpoint: string, options?: RequestOptions): Promise<T> => {
    return apiClient<T>(endpoint, { ...options, method: HTTP_METHODS.GET });
  },

  getPaginated: <T = any>(endpoint: string, options?: RequestOptions): Promise<PaginatedResponse<T>> => {
    return apiClient<PaginatedResponse<T>>(endpoint, { ...options, method: HTTP_METHODS.GET });
  },

  getBlob: (endpoint: string, options?: RequestOptions): Promise<Blob> => {
    return apiClient<Blob>(endpoint, { ...options, method: HTTP_METHODS.GET, responseType: 'blob' });
  },

  head: (endpoint: string, options?: RequestOptions): Promise<void> => {
    return apiClient<void>(endpoint, { ...options, method: HTTP_METHODS.HEAD });
  },

  post: <T = any>(endpoint: string, body?: any, options?: RequestOptions): Promise<T> => {
    return apiClient<T>(endpoint, {
      ...options,
      method: HTTP_METHODS.POST,
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  put: <T = any>(endpoint: string, body?: any, options?: RequestOptions): Promise<T> => {
    return apiClient<T>(endpoint, {
      ...options,
      method: HTTP_METHODS.PUT,
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  delete: <T = any>(endpoint: string, options?: RequestOptions): Promise<T> => {
    return apiClient<T>(endpoint, { ...options, method: HTTP_METHODS.DELETE });
  },

  patch: <T = any>(endpoint: string, body?: any, options?: RequestOptions): Promise<T> => {
    return apiClient<T>(endpoint, {
      ...options,
      method: HTTP_METHODS.PATCH,
      body: body ? JSON.stringify(body) : undefined,
    });
  },
};

export default api;
